/**
 * Murphy Door Configurator - Blueprint Geometry (PHASE 0: FOUNDATION)
 *
 * PURPOSE
 * Pure data for the measurement/blueprint view. Two independent halves:
 *
 *   traces[]       WHERE things sit on the 2048 canvas. Machine-derived by
 *                  scripts/trace-layer-silhouettes.mjs from the SAME layer PNGs
 *                  the configurator composites, so the line art registers with
 *                  the art it replaces. Regenerate whenever the 3D renders are.
 *
 *   trimProfiles[] HOW BIG things are in the real world, in inches. This is the
 *                  half a traced pixel can never answer -- see below.
 *
 * PHASE: 0 (Foundation) -- zero dependencies, no events, no DOM. Loads before
 * the event bus, same slot as md-config-core-constants.js.
 * CCP STATUS: EXEMPTED (pre-event-bus infrastructure, pure data).
 *
 * ---------------------------------------------------------------------------
 * WHY INCHES CANNOT COME FROM PIXELS
 * ---------------------------------------------------------------------------
 * The base render is a FIXED 38"x82" rough opening and the art is schematic: a
 * 24" door and a 48" door composite to identical pixels and only the label
 * changes (see scripts/generate-dimension-overlays.mjs). So a traced span tells
 * you nothing about the customer's actual door.
 *
 * TRIM is the exception, and the reason trimProfiles has real numbers in it:
 * casing and entablature are FIXED physical sizes that do not scale with the
 * door, and the render has a known scale of 21.105 px/inch. So their dimensions
 * CAN be recovered from the renders -- and were, rather than invented.
 *
 * ---------------------------------------------------------------------------
 * 🚨 SOME trimProfiles VALUES ARE STILL PROVISIONAL. READ BEFORE CLEARING FLAGS.
 * ---------------------------------------------------------------------------
 * ⬛ CONFIRMED 2026-09-22 -- all four CASING profiles and the two STANDARD
 *    entablature profiles now carry engineering's own numbers, from
 *    "Bookcase Door Measurements.xlsx" -> sheet "Door Measurement Calculator"
 *    (R. Cooley), scope FMD2 SINGLE door. Stated tolerance +/-1/16in, except
 *    R.O.-to-casing-edge which the sheet notes at +/-5/16in.
 *
 * ⬛ CONFIRMED rev. 2026-09-25 evening -- the sheet gained an entablature
 *    drop-down (Flat = our Standard, Square, Crown) and a back-side "-1 RULE".
 *    Square and Crown FRONT width and FRONT/BACK heights are now sheet
 *    formulas; their `provisional` and `swingDependent` flags are CLEARED.
 *
 * ⬛ ADOPTED -- sheet C15 for the STANDARD back entablature width (5/8in
 *    past the back casing per side), so it can never read narrower than the
 *    back casing it sits on.
 *
 * ⬛ CONFIRMED rev. 2026-09-28 -- the BACK entablature WIDTH (sheet C15) for
 *    SQUARE and CROWN. Formula: C15 = IF(Style_ENT="Square", OAW_Case_B+1.25+1.5,
 *    IF(Style_ENT="Crown", OAW_Case_B+1.25+5.75, OAW_Case_B+1.25)), where
 *    OAW_Case_B (C14) = Width_Nom + 2*Width_CASE and R.O. = Width_Nom + 2.
 *    Per side over the R.O. that is Square 4.125/5.125 (normal/wide) and
 *    Crown 6.25/7.25 -- replacing the render-traced fixed spreads 4.69/7.16.
 *    `widthProvisional` is CLEARED on both entries; the traced SVG artwork
 *    (traces[] above, heightIn) remains drawing-only and is untouched.
 *
 * The remaining render-traced values were measured off 3D RENDERS, not off the
 * shop floor. A render can be modelled slightly loose and still look perfect;
 * a customer who cuts an opening to a number we invented does not get a refund
 * for our confidence.
 *
 * `provisional: true` remains the RECORD OF WHAT IS UNVERIFIED. It no longer
 * hides the dimension: since 2026-09-04 the blueprint SHOWS provisional numbers
 * to customers, by the product owner's explicit decision that the render-derived
 * values are close enough to ship while engineering confirms them.
 *
 * So these flags are still load-bearing, just for a different job -- they mark
 * which numbers are still owed a confirmation. Suppress them again with
 * ?mdBlueprintProvisional=0, or permanently by flipping the default in
 * includeProvisional() in md-config-ui-blueprint-overlay.js.
 *
 * WHEN ENGINEERING CONFIRMS (see ~/Downloads/murphy-door-trim-dimension-questions.txt):
 *   1. Replace the number.
 *   2. Delete that entry's `provisional: true`.
 *   3. Nothing else changes -- the callout is already rendering; clearing the
 *      flag is what records that its value is now trustworthy.
 * Do NOT bulk-clear the flags. Clear each one as its number is confirmed.
 *
 * KNOWN GAPS the renders could NOT answer:
 *   - SWING -- RESOLVED for every style (Standard 2026-09-22, Square/Crown
 *     rev. 2026-09-25 evening): the sheet gives in-swing (row 19) and
 *     out-swing (row 20) separately, so every entablature carries a
 *     {inswing, inswingWide, outswing, outswingWide} object.
 *   - Whether these are nominal or actual dimensions.
 *   - Whether the casing head (absent from the render -- the casing layer is
 *     legs only, verified: zero ink on the centre column) exists as separate
 *     geometry or is part of the entablature.
 *
 * DERIVATION RECORD (so a future reader can re-check rather than re-guess):
 *   PPI            = (1425 - 623) / 38 = 21.105 px/in
 *   casing legs    = horizontal ink run, constant across rows 300..1900
 *                    front 81px -> 3.84in, back 83px -> 3.93in
 *   casing spread  = (outer span / PPI - 38) / 2
 *                    front (1440-584)=856px -> 40.56in -> 1.28in per side
 *                    back  (1496-552)=944px -> 44.73in -> 3.37in per side
 *   entablature    = vertical ink extent, and top edge vs OPENING.T
 *   CROSS-CHECK    = traced front casing outer edge x=583 lands exactly on
 *                    DOOR_UNIT.L (583), which was measured independently from
 *                    live configurator screenshots in Aug 2026. Two unrelated
 *                    methods, same pixel -- strong evidence both are right.
 */
(function (window) {
  'use strict';

  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};

  /** Layer PNGs are 2048 square. Their FILENAMES claim 2880 and are wrong. */
  const CANVAS = 2048;

  /** Rough opening in the base photo, Sobel-measured. Shared with the overlay generator. */
  const OPENING = { L: 623, R: 1425, T: 226, B: 1957 };

  /** Composited door unit (slab + jamb + casing). Wider than OPENING. */
  const DOOR_UNIT = { L: 583, R: 1439 };

  /** Isotropic on both axes, which is how we know the render is orthographic. */
  const PIXELS_PER_INCH = (OPENING.R - OPENING.L) / 38;

  /** The base photo is always this opening, whatever the customer picked. */
  const BASE_RENDER_RO = { widthIn: 38, heightIn: 82 };

  // -------------------------------------------------------------------------
  // Traced silhouettes -- PLACEMENT ONLY, never measurement.
  // Generated by: node scripts/trace-layer-silhouettes.mjs --trace
  // -------------------------------------------------------------------------
  const traces = {
    'back-casing-fluted': {
      d: 'M552,240L632,240L632,1972L552,1972L552,240ZM1412,240L1492,240L1492,1972L1412,1972L1412,240Z',
      bbox: { L: 552, R: 1496, T: 240, B: 1976 },
    },
    'back-casing-standard': {
      d: 'M552,240L632,240L632,1972L552,1972L552,240ZM1412,240L1492,240L1492,1972L1412,1972L1412,240Z',
      bbox: { L: 552, R: 1496, T: 240, B: 1976 },
    },
    'back-entablature-crown': {
      d: 'M472,60L1572,60L1572,68L1520,124L1520,136L1504,152L1504,220L1520,228L1516,240L528,240L524,228L540,220L540,152L524,136L524,124L480,80L472,60Z',
      bbox: { L: 472, R: 1576, T: 60, B: 244 },
    },
    'back-entablature-square': {
      d: 'M524,124L1520,124L1520,140L1504,144L1504,216L1520,220L1520,236L528,240L524,220L540,216L540,144L524,140L524,124Z',
      bbox: { L: 524, R: 1524, T: 124, B: 244 },
    },
    'back-entablature-standard': {
      d: 'M544,156L1500,156L1504,236L544,240L544,156Z',
      bbox: { L: 540, R: 1508, T: 156, B: 244 },
    },
    'bottom-section-standard': {
      d: 'M716,1552L1304,1552L1316,1584L704,1584L704,1568L716,1552Z',
      bbox: { L: 704, R: 1320, T: 1552, B: 1588 },
    },
    'carcass-back': {
      d: 'M620,224L1424,224L1424,1972L1412,1980L632,1980L620,1972L620,224ZM680,256L640,256L644,1940L680,1936L680,256ZM1404,256L1396,252L1388,260L1396,1940L1404,1936L1404,256ZM644,256L680,260L676,1936L644,1936L644,256Z',
      bbox: { L: 620, R: 1428, T: 224, B: 1984 },
    },
    'carcass-front': {
      // DE-NOISED 2026-09-02. The tracer emitted the latch-side reveal TWICE --
      // x1364..1396 and x1368..1392, the same edge -- which drew four parallel
      // lines inside 1.5in and read as a bundle of artefacts. The surviving
      // strip then closed at y1910, 46 units shy of the floor, so its bottom cap
      // hung in mid-air as a small floating rectangle. Both dropped: the casing
      // legs already carry that read, and the slab is one clean rectangle.
      // Raw trace preserved at scratch/paths.raw.json.
      d: 'M628,240L1416,240L1416,1956L628,1956L628,240Z',
      bbox: { L: 628, R: 1420, T: 240, B: 1956 },
    },
    'door-backing-flat': {
      d: 'M744,244L1348,244L1348,1812L740,1808L744,244Z',
      bbox: { L: 740, R: 1352, T: 244, B: 1816 },
    },
    'door-backing-mirror': {
      d: 'M700,236L1372,236L1372,1968L700,1968L700,236Z',
      bbox: { L: 700, R: 1376, T: 236, B: 1972 },
    },
    'front-casing-fluted': {
      // Bottoms squared off 2026-09-02: the trace tapered 4 units at the
      // floor (660->656, 1436->1432, 1956->1952), leaving a wedge that is
      // invisible at 435px and a distinct artefact at any zoom.
      d: 'M584,268L660,268L660,1956L584,1956L584,268ZM1356,268L1436,268L1436,1956L1356,1956L1356,268Z',
      bbox: { L: 584, R: 1440, T: 268, B: 1960 },
    },
    'front-casing-standard': {
      // Bottoms squared off 2026-09-02: the trace tapered 4 units at the
      // floor (660->656, 1436->1432, 1956->1952), leaving a wedge that is
      // invisible at 435px and a distinct artefact at any zoom.
      d: 'M584,268L660,268L660,1956L584,1956L584,268ZM1356,268L1436,268L1436,1956L1356,1956L1356,268Z',
      bbox: { L: 584, R: 1440, T: 268, B: 1960 },
    },
    'front-entablature-crown': {
      d: 'M504,92L1512,92L1512,100L1444,184L1444,244L1464,260L1456,268L560,268L556,252L572,248L572,184L504,92Z',
      bbox: { L: 504, R: 1516, T: 92, B: 272 },
    },
    'front-entablature-square': {
      d: 'M556,152L1460,152L1460,172L1444,176L1444,244L1460,248L1464,260L1456,268L560,268L556,248L572,244L572,176L556,172L556,152Z',
      bbox: { L: 556, R: 1468, T: 152, B: 272 },
    },
    'front-entablature-standard': {
      d: 'M572,188L1444,188L1444,264L576,268L572,188Z',
      bbox: { L: 572, R: 1448, T: 188, B: 272 },
    },
    'top-section-standard': {
      d: 'M704,648L1316,648L1316,664L1304,676L716,676L704,664L704,648ZM704,956L1316,956L1316,968L712,972L704,968L704,956Z',
      bbox: { L: 704, R: 1320, T: 648, B: 976 },
    },  };

  // -------------------------------------------------------------------------
  // Trim profiles -- REAL-WORLD INCHES. Sheet-sourced unless flagged. See header.
  //
  // Casing entries:
  //   legFaceWidthIn   visible width of one leg. Object {normal,wide} on
  //                    sheet-confirmed entries -- the "-1 RULE" widens it.
  //   roToCasingEdgeIn how far the casing outer edge sits beyond the R.O. edge,
  //                    PER SIDE: {hinge, hingeWide, nonHinge, nonHingeWide?,
  //                    nonHingeUnderRule?}. nonHingeWide applies when the casing
  //                    widens (absent = nonHinge); nonHingeUnderRule applies
  //                    when the rule is met but THIS casing is wideRuleExempt
  //                    (absent = nonHinge). Asymmetric on the front by design --
  //                    see WHICH SIDE IS WHICH below.
  //   wideRuleExempt   true = never widens under the "-1 RULE" (Fluted: vendor-
  //                    milled, always 3-3/4in). Read by the dimension calculator.
  //   spreadPerSideIn  legacy symmetric spread. Only on entries the sheet has
  //                    NOT confirmed; the calculator falls back to it.
  //   runsToFloor      legs reach the floor (verified: ink to y=1958 ~ OPENING.B)
  //   hasHead          the render's casing layer is LEGS ONLY, no head piece
  //
  // Entablature entries:
  //   heightIn         vertical extent of the moulding itself. NOT sheet-derived
  //                    even on confirmed entries -- still render-traced, and not
  //                    read by any callout. Kept for drawing only.
  //   topAboveRoIn     top edge above the R.O. head -- the number that drives
  //                    floor-to-top-of-entablature. Object
  //                    {inswing,inswingWide,outswing,outswingWide} once
  //                    confirmed; a bare number means still swing-blind.
  //   widthOverCasingIn FRONT only: entablature width = front casing-to-casing
  //                    width + this (sheet B15).
  //   spreadPerSideIn  BACK only: overhang beyond the R.O. edge per side; width
  //                    = R.O. + 2*spread. Object {normal,wide} or a bare number.
  //   widthProvisional true = the WIDTH on this entry is still unconfirmed.
  //                    No profile currently sets this (back Square/Crown were
  //                    the last holdouts; sheet C15 confirmed rev. 2026-09-28).
  //                    Scoped to width only; the heights are sheet-sourced.
  //                    Left in place, generically supported, for a future
  //                    unconfirmed profile to flag itself.
  //   provisional /    whole-entry flags, now on NO entry. Still honoured by
  //   swingDependent   the calculator should a future unconfirmed profile need
  //                    them.
  //
  // -------------------------------------------------------------------------
  // WHICH SIDE IS WHICH (load-bearing for the asymmetric front casing)
  // -------------------------------------------------------------------------
  // The base art is authored LEFT-HAND: the hinge sits on the LEFT, and the
  // front casing overlaps MORE on the hinge side (confirmed off two FBX models,
  // see drawSilhouettes() in md-config-ui-blueprint-overlay.js). The engineering
  // sheet agrees independently -- front hinge 1.75in vs non-hinge 1.5in. A
  // right-hand door is the mirror image, which is why the calculator keys the
  // per-side assignment off ctx.hand and suppresses the pair when hand is
  // unknown rather than guessing a side.
  //
  // SOURCE OF TRUTH for every number below marked "sheet":
  //   "Bookcase Door Measurements.xlsx" -> sheet "Door Measurement Calculator",
  //   R. Cooley, rev. 2026-09-22 (Fluted "-1 RULE" exemption: rev. 2026-09-25;
  //   back -1 rule, Square/Crown entablature: rev. 2026-09-25 evening).
  //   Scope: FMD2 SINGLE width door ONLY.
  //   Stated tolerance +/-1/16in, except R.O.-to-casing-edge at +/-5/16in.
  // -------------------------------------------------------------------------
  const trimProfiles = {
    /* CASING -- sheet-confirmed, provisional flags CLEARED. Sheet rows cited
       as of rev. 2026-09-25 evening ("c"): Width_CASE (B10) is 4-3/4in when
       the "-1 RULE" is met AND the casing is Flat (our Standard), else 3-3/4in.
       Front is ASYMMETRIC: B17 hinge = Width_CASE - 2 (1.75, or 2.75 wide);
       B18 non-hinge = B14 - R.O. - hinge = 1.5in -- EXCEPT a Fluted casing
       under the rule, where B14 takes the slab -1 without the casing +1, so the
       non-hinge reveal drops to 0.5in (see front-casing-fluted).
       Back is symmetric: C17 = C18 = Width_CASE - 1 (2.75, or 3.75 wide), so
       C14 back overall width = W + 2*Width_CASE. Until rev. "c" the back was a
       fixed 2.75/side (W+7.5) regardless of the rule. */
    'front-casing-standard': {
      legFaceWidthIn: { normal: 3.75, wide: 4.75 },
      roToCasingEdgeIn: { hinge: 1.75, hingeWide: 2.75, nonHinge: 1.5 },
      runsToFloor: true, hasHead: false,
    },
    'front-casing-fluted': {
      // Same 3-3/4in footprint as Standard (byte-identical bbox and ink runs),
      // but EXEMPT from the "-1 RULE" widening (Cooley, rev. 2026-09-25):
      // fluted casing is milled by an outside vendor and is ALWAYS 3-3/4in.
      // wideRuleExempt makes the calculator keep the 3-3/4in geometry for every
      // casing-driven dimension (hinge reveal, overall height); the "wide"
      // values below equal "normal" so the profile tells the same story on its
      // own. The slab -1 still applies.
      //
      // nonHingeUnderRule: sheet B14 gives Fluted under the rule W + 5.25 - 1
      // (the slab narrows, the casing does not widen), and B18 derives the
      // non-hinge reveal as B14 - R.O. - B17 = (W+4.25) - (W+2) - 1.75 = 0.5.
      // Adopted as the sheet states it (rev. 2026-09-25 evening).
      wideRuleExempt: true,
      legFaceWidthIn: { normal: 3.75, wide: 3.75 },
      roToCasingEdgeIn: { hinge: 1.75, hingeWide: 1.75, nonHinge: 1.5, nonHingeUnderRule: 0.5 },
      runsToFloor: true, hasHead: false,
    },
    'back-casing-standard': {
      // C17/C18 = Width_CASE - 1 on BOTH sides, so the back follows the rule.
      legFaceWidthIn: { normal: 3.75, wide: 4.75 },
      roToCasingEdgeIn: { hinge: 2.75, hingeWide: 3.75, nonHinge: 2.75, nonHingeWide: 3.75 },
      runsToFloor: true, hasHead: false,
    },
    'back-casing-fluted': {
      // Fluted is always 3-3/4in (Width_CASE 3.75), so C17/C18 stay 2.75 and
      // C14 stays W + 7.5 under the rule too.
      wideRuleExempt: true,
      legFaceWidthIn: { normal: 3.75, wide: 3.75 },
      roToCasingEdgeIn: { hinge: 2.75, hingeWide: 2.75, nonHinge: 2.75 },
      runsToFloor: true, hasHead: false,
    },

    /* ENTABLATURE -- Standard, Square and Crown are all sheet-sourced as of
       rev. 2026-09-25 evening (the sheet gained an entablature drop-down; its
       "Flat" is our Standard). Numbers below are INCHES ABOVE THE R.O.
       (H + 2), so each is the sheet's H-offset minus 2:

         front (B19 in / B20 out)       back (C19 in / C20 out)
         Flat   in H+2.75 | H+3.75 wide   in H+Width_CASE+0.125
                out H+Width_CASE-0.5        out H+Width_CASE-0.5
         Square in H+4.25, out H+4.75     in H+5.375, out H+4.75
         Crown  in H+6.75, out H+7.25     in H+7.875, out H+7.25

       Square and Crown heights do not depend on Width_CASE, so their wide and
       normal values are equal by design, not by omission.

       FRONT WIDTH (B15) is stated off the front casing, not the R.O.:
       frontOAW (B14) + widthOverCasingIn -- Flat +1.25, Square +2.75, Crown +7.

       BACK WIDTH (C15), sheet formula per style (rev. 2026-09-28):
         C15 = IF(Style_ENT="Square", OAW_Case_B+1.25+1.5,
               IF(Style_ENT="Crown",  OAW_Case_B+1.25+5.75,
                                       OAW_Case_B+1.25))
       where OAW_Case_B (C14) = Width_Nom + 2*Width_CASE and R.O. = Width_Nom+2.

       STANDARD ("Flat") reduces to W + 2*(Width_CASE + 0.625), i.e. 5/8in past
       the back casing per side -- per side over the R.O. that is
       Width_CASE - 0.375 -> 3.375 normal, 4.375 wide. Before, Standard shipped
       W + 8.5, which under the -1 rule read NARROWER than the back casing
       (W + 9.5) -- an impossible drawing. ADOPTED rev. 2026-09-25 evening.

       SQUARE reduces to per side over the R.O. Width_CASE + 0.375 -> 4.125
       normal, 5.125 wide (was a fixed 4.69, widthProvisional).
       CROWN reduces to per side over the R.O. Width_CASE + 2.5 -> 6.25
       normal, 7.25 wide (was a fixed 7.16, widthProvisional).
       Both CONFIRMED rev. 2026-09-28 (Cooley); the traced SVG art (traces[]
       above, heightIn here) remains drawing-only and is untouched. */
    'front-entablature-standard': {
      heightIn: 3.89,
      widthOverCasingIn: 1.25,
      topAboveRoIn: {
        inswing: 0.75, inswingWide: 1.75,
        outswing: 1.25, outswingWide: 2.25,
      },
    },
    'front-entablature-square': {
      heightIn: 5.50,
      widthOverCasingIn: 2.75,
      topAboveRoIn: {
        inswing: 2.25, inswingWide: 2.25,
        outswing: 2.75, outswingWide: 2.75,
      },
    },
    'front-entablature-crown': {
      heightIn: 8.48,
      widthOverCasingIn: 7,
      topAboveRoIn: {
        inswing: 4.75, inswingWide: 4.75,
        outswing: 5.25, outswingWide: 5.25,
      },
    },
    'back-entablature-standard': {
      // Width: sheet C15 (rev. 2026-09-25 evening) W + 2*(Width_CASE + 0.625)
      // -> R.O. + 2*3.375 (W + 8.75), or R.O. + 2*4.375 (W + 10.75) when the
      // back casing widens. Was a fixed 3.25 (W + 8.5).
      heightIn: 3.98,
      spreadPerSideIn: { normal: 3.375, wide: 4.375 },
      topAboveRoIn: {
        inswing: 1.875, inswingWide: 2.875,
        outswing: 1.25, outswingWide: 2.25,
      },
    },
    'back-entablature-square': {
      // Width: sheet C15 (rev. 2026-09-28) OAW_Case_B+1.25+1.5 -> per side
      // over the R.O., Width_CASE + 0.375 -> 4.125 normal, 5.125 wide.
      heightIn: 5.64, spreadPerSideIn: { normal: 4.125, wide: 5.125 },
      topAboveRoIn: {
        inswing: 3.375, inswingWide: 3.375,
        outswing: 2.75, outswingWide: 2.75,
      },
    },
    'back-entablature-crown': {
      // Width: sheet C15 (rev. 2026-09-28) OAW_Case_B+1.25+5.75 -> per side
      // over the R.O., Width_CASE + 2.5 -> 6.25 normal, 7.25 wide.
      heightIn: 8.67, spreadPerSideIn: { normal: 6.25, wide: 7.25 },
      topAboveRoIn: {
        inswing: 5.875, inswingWide: 5.875,
        outswing: 5.25, outswingWide: 5.25,
      },
    },
  };

  /**
   * Categories whose selection means the customer is thinking about SIZE.
   *
   * Read from the PPD `layerRole` field when present (configurator_tab_categories
   * -> layer_role). This literal is the fallback for the window between the
   * theme deploy and the metaobject being populated -- during which every one of
   * the 23 records returns null and the feature would otherwise be inert.
   *
   * Delivery and Extras are deliberately ABSENT despite sharing a tab with these:
   * the classification is per-category, never per-tab.
   */
  const MEASUREMENT_CATEGORIES = Object.freeze([
    'Width',
    'Height',
    'Door Depth',
    'Jamb Depth',
    'Door Handing',
  ]);

  // -------------------------------------------------------------------------
  // Sheet layout -- FIXED canvas coordinates.
  //
  // Every span below is pinned to the ARTWORK, never scaled by the value it
  // reports. The first cut scaled each span by valueIn / BASE_RENDER_RO, so a
  // 48in door drew a brace wider than the door it was measuring; only the base
  // 38x82 render lined up. Spans are art; only the NUMBERS change.
  // -------------------------------------------------------------------------

  /** Label columns. Text is flush to the drawing: right-aligned left, left-aligned right. */
  const GUTTER_L = 558;
  const GUTTER_R = 1545;

  /** Elevation braces. */
  const BRACE = Object.freeze({
    RO_H_Y: OPENING.T + 52,       // rough-opening horizontal arm
    RO_V_X: OPENING.L + 52,       // rough-opening vertical arm
    DOOR_H_Y: OPENING.T + 142,    // door arm, nested one step inside the R.O. arm
    DOOR_V_X: OPENING.L + 142,
    OVERALL_W_Y: 1994,            // floor band, casing edge to casing edge
    OVERALL_H_X: 1466,            // right wall lane, crown top to floor
  });

  // -------------------------------------------------------------------------
  // Plan detail.
  //
  // Jamb depth and door depth do not EXIST in a front elevation -- which is why
  // the configurator ships no layer art for any of the five door depths or
  // either jamb depth. The plan is the only view where both are real, so the
  // handing glyph grows into a dimensioned detail carrying handing, jamb depth,
  // door depth and pass-through.
  //
  // PS is its own scale, declared on the drawing. At elevation scale (21.105
  // u/in) a 36in leaf swung open needs 760 units of vertical room and there are
  // 550. 13 is a floor, not a preference: at 8 u/in an 8-1/4in door drew a
  // 66-unit leaf -- 14px, a hairline -- and its THICKNESS is the dimension.
  //
  // PCX is pushed right so the detail clears the elevation's own dimension arms
  // at x675 and x765 rather than sitting on top of them.
  // -------------------------------------------------------------------------
  const PLAN = Object.freeze({
    /* Upper bound on the plan scale, not a fixed one -- see MIN_LEAF_PX. A 49in
       door at 13 u/in needs 847 units of width and the door interior is 792, so
       the widest doors scale down to fit. */
    SCALE: 13.0,          // units per inch, plan detail only
    /* The leaf's thickness IS the door depth, so it has a legibility floor: at
       8 u/in the shallowest door drew a 42-unit leaf, 9px at 435, a hairline. */
    MIN_LEAF_PX: 12,
    CX: 1104,
    /* The wall's y is COMPUTED per swing, not fixed. It used to be a constant
       1560, which works for in-swing (the leaf rises from it and there is room
       above) and breaks out-swing outright: the leaf then falls BELOW it, and a
       38in door needs 494 units where only 488 exist. Measured on the live dev
       theme, an out-swing leaf ran to y2113 against a 2048 canvas -- 157 units
       off the bottom of the drawing, through the Front/Back switch.
       BAND is the vertical room the figure gets; the wall sits at whichever end
       leaves the leaf its swing side. BAND_BOTTOM stops short of the carcass
       floor so the handing caption clears that switch. */
    BAND_TOP: 860,
    BAND_BOTTOM: 1800,
    /* Wall returns, symmetric. An earlier asymmetric pair (140 left / 70 right)
       bought the jamb leader landing room, but it also pushed the figure off
       centre and cost 70 units of the width budget -- which is what made a 49in
       door overflow the door interior. The leader gets its clearance from
       landing on the return's OUTER end instead. */
    RET: 70,
  });

  // -------------------------------------------------------------------------
  // Birdseye frame.
  //
  // The plan detail used to be drawn INSIDE the elevation, which meant it had
  // to fit between the door's own edges: 624 units, and a hard cap of 13 u/in.
  // At that cap the shallowest door drew its leaf 12.3px wide on a 370px
  // container against a 12px floor -- no headroom at all, measured.
  //
  // Given its own frame the constraint is the FRAME, not the door, so the same
  // drawing gets 1,448 units between the gutters and roughly doubles in scale.
  // Nothing about the geometry changed: drawPlanDetail takes a layout box and
  // this is simply a bigger one.
  //
  // 🚨 SCALE IS STILL A CAP, NOT A FIXED VALUE. A 49in door at 26 u/in needs
  // 1,274 units of leaf and only 1,188 are available between the returns, so
  // the widest doors scale down to fit exactly as they did before.
  // -------------------------------------------------------------------------
  const BIRDSEYE = Object.freeze({
    SCALE: 26.0,           // u/in cap -- 2x the in-elevation cap
    CX: CANVAS / 2,
    BAND_TOP: 420,
    /* 🚨 1820 put NARROW doors through the controls. In-swing anchors the wall
       at max(BAND_TOP + leafLen, BAND_BOTTOM - depth - jamb); for a small door
       the SECOND term wins, so a low band bottom drags the figure down no
       matter what the scale cap says. Measured, a 24in door sat its wall at
       y1513 and its caption at y1773, through the frame switch at y1615. */
    BAND_BOTTOM: 1450,
    RET: 130,              // wall returns, scaled with the drawing
    /* The plan's width fence. Text does NOT sit in these -- see LABEL_* below. */
    GUTTER_L: 300,
    GUTTER_R: 1748,
    /* 🚨 Birdseye text anchors at the FRAME EDGE and runs INWARD, unlike the
       elevation where it runs outward into the margin. Measured on the live dev
       theme: anchoring "Pass-through" at GUTTER_R with text-anchor=start ran it
       to x2106 on a 2048 canvas -- 58 units off the drawing, clipped. The
       birdseye labels sit above and below the figure rather than beside it, so
       there is no margin out there to run into. */
    LABEL_L: 54,
    LABEL_R: 1994,
    /* The wall runs the full width of the frame rather than stopping a short
       return either side. Drawn to these absolute x's, NOT derived from RET, so
       a longer wall costs the plan no scale -- RET still fences the figure. */
    /* 🚨 MEASURED EXCLUSION ZONES, not chosen for looks. The slide carries three
       overlay controls, and the plan has to clear all of them:
         zoom buttons    x1784..1992, y1332..1992
         frame switch    x24..499,   y1615..1841
         measure toggle  x24..499,   y1841..2048
         Front/Back                  y1841..2048
       The wall ran to x1988 and passed straight behind the zoom column. 1740
       clears it with margin, and 308 keeps the wall symmetric about CX. */
    WALL_L: 308,
    WALL_R: 1740,
    /* The figure AND its handing caption must finish above the frame switch.
       Used to cap the scale vertically -- see SAFE_BOTTOM in the overlay. */
    SAFE_BOTTOM: 1600,
    WALL_LABEL: 'WALL',
    DOOR_LABEL: 'DOOR',
    FRAME_INSET: 34,       // hairline border inset from the canvas edge
  });

  /**
   * Which silhouettes the blueprint draws. Deliberately NOT every trace:
   * the shelf sections are long thin rectangles that read as artefacts in line
   * art, and dropping them is what freed the door interior for the plan detail.
   */
  const BLUEPRINT_SILHOUETTES = Object.freeze([
    'entablature', 'casing', 'carcass',
  ]);

  /**
   * Where each callout's text block and leader dot live.
   *
   * `ay` is the TITLE baseline. `dot` is a fixed canvas point; `dotFrom` names a
   * point the UI resolves against the live plan detail instead, because those
   * three move with the selection (a 12in door draws a thicker leaf than a
   * 5-1/4in one). Semantic names, not coordinates -- same pattern the leader
   * anchors already use, so a re-trace moves the arrows without touching logic.
   */
  const CALLOUT_SLOTS = Object.freeze({
    // --- ELEVATION frame: the four authoritative dimensions, unchanged ---
    'ro-size':           { frame: 'elevation', side: 'left',  ay: 420,  dot: { x: 675, y: 700 } },
    'door-size':         { frame: 'elevation', side: 'left',  ay: 860,  dot: { x: 765, y: 1040 } },
    'overall-height':    { frame: 'elevation', side: 'right', ay: 420,  dot: { x: 1466, y: 580 } },
    // dotRidesRule: this dot lands ON the floor-band rule, which the UI lifts
    // clear of the gallery's bottom control row. Authored y is the uncapped
    // position; the renderer owns the cap so the two cannot drift apart.
    'overall-width':     { frame: 'elevation', side: 'right', ay: 1700, dot: { x: 1150, y: 1994 }, dotRidesRule: true },
    /* Entablature width -- the horizontal span of the head moulding itself,
       always wider than overall-width (the entablature flares past the casing
       it sits on, see the "CASING TO CASING, AND ONLY THE CASING" note in
       md-config-bl-dimension-calculator.js).

       🚨 dotRidesEntablatureRule, not a fixed dot -- CORRECTED 2026-09-12. The
       fixed y70 point this used to carry (see git history) sat above even the
       tallest entablature style's own traced top edge (crown bbox.T is 92),
       so the leader pointed at blank canvas above the art -- reported live as
       "not helpful". There was also no drawn RULE for this dimension at all,
       unlike every other elevation measurement (rough-opening, door-unit,
       overall-height, overall-width all have a real span with tick marks the
       dot lands on). Both are fixed together: drawBraces now draws an actual
       entablature-width rule spanning the SELECTED entablature trace's own
       L/R edges, positioned above its own top edge (see
       entablatureWidthRuleY's doc comment in md-config-ui-blueprint-overlay.js
       for why this has to be style-dependent -- crown/square/standard trace
       to genuinely different heights), and this dot rides that rule's y the
       same way 'overall-width' already rides the floor band. `dot.y` here is
       now just a placeholder (the renderer always overrides it when an
       entablature trace exists, and drops the callout entirely when one
       doesn't -- see dotRidesEntablatureRule's handling in drawLabels).
       `dot.x` is real: 620 sits inside every style's traced width (narrowest,
       Standard, is L572..R1448), so it lands ON the rule regardless of which
       style is selected. */
    'entablature-width': { frame: 'elevation', side: 'left',  ay: 130,  dot: { x: 620, y: 130 }, dotRidesEntablatureRule: true },
    /* Rough-opening-to-casing-edge -- one dot per side, both fed the SAME
       spreadPerSideIn number. Real casing overlap is asymmetric (more on the
       hinge side -- confirmed off actual CAD geometry, see the mirror-for-hand
       note in md-config-ui-blueprint-overlay.js), but nobody has confirmed a
       precise per-side split yet, so this reports the one number engineering
       HAS confirmed (Part 1 of the trim questionnaire) on both leaders rather
       than inventing an asymmetric magnitude. Dots sit on the front casing
       trace's own edges (front-casing-standard bbox), the same fixed reference
       'overall-width' already uses regardless of which casing style or view is
       actually showing.

       🚨 THE TWO SIDES ARE NOT SYMMETRIC ay VALUES, AND THAT IS DELIBERATE.
       The right gutter is the one sharing chrome with the gallery's zoom
       column (see 'overall-width's dotRidesRule comment and drawLabels'
       zoomTop clamp) -- an authored ay past that clamp's ceiling gets pulled
       UP to the ceiling, not held at its authored position. 'ro-to-casing-
       edge-right' at ay 1450 collided with 'overall-width' (ay 1700) doing
       exactly that: both got clamped to within 2 canvas units of each other
       and rendered as unreadable overlapping text -- shipped live 2026-09-11,
       caught in prod UAT. Two more attempts (ay 1000, then 750) each measured
       clear against jsdom's own canonical test shapes (435x435, 400x400) but
       still read as visually touching in a REAL rendered browser at a taller
       container -- 'overall-width's clamped ceiling moves with fittedPx, and
       a gap that is comfortable at one aspect ratio is not automatically
       comfortable at another. ay 700 was verified BOTH ways: jsdom-clear at
       435x435 and 400x400 (see tests/assets/blueprint-overlay-toggle.test.js,
       "elevation right gutter keeps its blocks apart"), and visually clear in
       a real rendered browser at a taller/narrower container where 750 still
       touched. The left gutter carries no such chrome, so 'ro-to-casing-edge-
       left' keeps its own authored position (1450, clear of door-size's block
       ending ~942) -- the two sides render at different heights, which is a
       cosmetic asymmetry, not a bug: they are independent leaders pointing at
       different X positions on the sheet, not one dimension split in two.

       🚨 A PRE-EXISTING, SEPARATE COLLISION AT EXTREME ASPECT RATIOS: at a
       very wide-and-short container (766x235, one of the shapes the "elevation
       clears the gallery chrome" tests already exercise), 'overall-height'
       (ay 420, previously assumed always safely above the clamp) and
       'overall-width' (ay 1700) both clamp to the SAME ceiling and collide --
       with or without this callout in the mix. Confirmed via the same jsdom
       harness; not introduced by this change, not fixed by it either. Flagging
       here since the next person tuning this gutter needs to know two
       independent collisions are possible, not one. */
    // dotRidesCasingEdge: CORRECTED 2026-09-12 -- see elevationGutters' doc
    // comment in md-config-ui-blueprint-overlay.js. The x below is the FRONT
    // casing's edge (front-casing-standard bbox L584/R1440) and is now only
    // the fallback used before a casing selection resolves; the renderer
    // substitutes the SELECTED view's actual casing edge (back casing traces
    // wider, L552/R1496) before mirroring.
    'ro-to-casing-edge-left':  { frame: 'elevation', side: 'left',  ay: 1450, dot: { x: 584, y: 1000 }, dotRidesCasingEdge: 'L' },
    'ro-to-casing-edge-right': { frame: 'elevation', side: 'right', ay: 700,  dot: { x: 1440, y: 1000 }, dotRidesCasingEdge: 'R' },
    // --- BIRDSEYE frame: the three that only exist in plan ---
    // They keep their leader anchors; only the text block moves, because the
    // frame's gutters are wider and there is no elevation art to dodge.
    'door-depth':        { frame: 'birdseye',  side: 'left',  ay: 300,  dotFrom: 'plan:leaf-top' },
    'jamb-depth':        { frame: 'birdseye',  side: 'left',  ay: 1610, dotFrom: 'plan:left-return' },
    /* valueInArt, same reason as wall-clearance directly below: the isometric
       already states the number itself, on the floor rug's .ptw-rug-label, so
       the block supplies only the title ("Pass-through space") and a leader
       to it -- printing the value a second time in the margin would say it
       twice within one glance. The base side/ay here are the in-swing
       arrangement; BIRDSEYE_SLOTS_BY_HANDING below overrides both per handing
       so the block clears the OTHER birdseye blocks at that hand+swing rather
       than landing on top of one of them. */
    'pass-through':      { frame: 'birdseye',  side: 'right', ay: 300,  dotFrom: 'plan:pass-span', valueInArt: true },
    /* Wall clearance is annotated by the isometric too, for the same reason
       pass-through now is: the pass-through band is a wide floor rug the eye
       reads as an opening on its own, while the clearance is a bare number
       over a 24-unit dimension arrow at the head of the door and names nothing
       without a title next to it -- so both get a titled block and a leader,
       and the in-art number stays as the compact repeat at the point of
       measurement.

       These base values are the in-swing arrangement; BIRDSEYE_SLOTS_BY_HANDING
       overrides the side per handing, because the block has to end up OPPOSITE
       door depth or the two leaders cross over this very annotation. They are
       authored rather than left undefined so a handing with no override degrades
       to a placed block instead of NaN coordinates.

       On the DRAWN-PLAN fallback this still draws nothing: drawPlanDetail
       publishes no 'plan:wall-clearance' anchor, and drawLabels skips a callout
       whose dot it cannot resolve. The plan has no clearance art to point at. */
    'wall-clearance':    { frame: 'birdseye',  side: 'left',  ay: 375,  dotFrom: 'plan:wall-clearance', valueInArt: true },
    /* Swing clearance (sheet B24): wall to the furthest point of the OPEN door.
       NOT wall clearance (B25) -- a separate block with its own anchor on the
       open leaf's far edge. The value IS printed in the block (no valueInArt):
       the CAD carries no annotation for this dimension. Base side/ay are the
       lh-in arrangement; BIRDSEYE_SLOTS_BY_HANDING places it per handing. On
       the drawn-plan fallback there is no 'plan:swing-clearance' anchor, so
       drawLabels skips it, exactly as it does wall clearance. */
    'swing-clearance':   { frame: 'birdseye',  side: 'right', ay: 700,  dotFrom: 'plan:swing-clearance' },
  });

  /**
   * Which FRAME a measurement category wants when the customer touches it.
   *
   * Not an invention -- it falls straight out of CALLOUT_SLOTS. Door depth,
   * jamb depth and handing are the dimensions that only EXIST in plan, so they
   * are drawn in the birdseye; width and height are the elevation's own. A
   * customer who opens Jamb Depth is asking about the plan, so the plan is what
   * should be on screen.
   *
   * A category absent from this map leaves the frame alone.
   */
  /**
   * Per-handing overrides for the BIRDSEYE callout slots.
   *
   * 🚨 THE BASE SLOTS CANNOT SERVE ALL FOUR HANDINGS. CALLOUT_SLOTS parks each
   * text block at a fixed side + ay and lets mirrorSides flip the column, which
   * works while the ANCHORS mirror the same way. They do not: out-swing mirrors
   * the figure VERTICALLY (my()), so the door-depth anchor jumps from the leaf's
   * top edge to its bottom edge while the label stays parked at the top -- the
   * leader then rakes diagonally across the whole leaf and crosses the jamb
   * leader, which is the big X visible in both out-swing views.
   *
   * The slide's controls are also NOT mirror-symmetric, so a mirrored slot walks
   * a label straight under them: zoom occupies x1784..1992 / y1332..1992 (right),
   * and the two toggle rows occupy x24..499 / y1615..2048 (bottom left).
   *
   * Keys are `${hand}-${swing}` lowercased. `side` here is ABSOLUTE -- it is used
   * verbatim and mirrorSides is NOT applied on top, or the override would flip
   * out from under itself. `center` parks the block on CX for the cases where
   * either gutter would drag its leader back across the figure.
   *
   * 🚨 A CENTRED ay MUST CLEAR THE FRAME RULE. The centre column runs under
   * the title, not beside it, so a block that is fine in a gutter overlaps the
   * rule at the same ay. Measured on the preview theme: ay 250 put the block's
   * top at y203 against a rule at y221. A block's top is ay - ~47 at a 435px
   * container, so a centred slot needs ay >= ~290.
   */
  const BIRDSEYE_SLOTS_BY_HANDING = Object.freeze({
    /* ISO frame. The drawn plan was wide and low, so it could share the centre;
       the isometric OWNS the centre column, so both callouts sit in the gutters
       and each one is placed on the same side as the feature it names.

       🚨 DOOR DEPTH DID NOT ACTUALLY FOLLOW THAT RULE ON EITHER IN-SWING KEY,
       and nothing noticed until the clearance annotation was drawn into the gap
       it was crossing. Anchor sides, computed from ISO_ANCHORS through
       drawIsoDetail's own fit (centre x1024):

         lh-in   leaf-top x1153 RIGHT | left-return x1213 RIGHT | clearance x883  LEFT
         rh-in   leaf-top x895  LEFT  | left-return x836  LEFT  | clearance x1165 RIGHT

       Jamb depth was already beside its anchor on both. Door depth was parked in
       the OPPOSITE gutter, so its leader set off from one margin and traversed
       the whole figure -- and it happened to traverse it at the exact height of
       the clearance annotation. Measured on the right-hand door: that leader
       crossed x1165 at y425 while the "1 3/4″" glyph box occupied y405..471.

       So this is not a new exception, it is door depth finally obeying the rule
       above. Each in-swing block now sits on its own anchor's side of the
       centreline, no leader crosses the middle, and the arrangement mirrors with
       the handing. Out-swing keeps its own tuning -- it has no clearance
       annotation to collide with, an out-swing door swinging away from the wall. */
    'iso-lh-in':  {
      'door-depth':     { side: 'right', ay: 300 },
      /* SWING CLEARANCE (2026-09-25 evening) shares this gutter, between door
         depth and jamb depth. Door depth's four-line block ends ~566 (435px
         container) / ~613 (370px); a three-line block needs ~188 / ~220
         units. Jamb depth used to sit at 870, which left 213 units at 370px --
         too few -- so it moved DOWN to 1060, which also levels its own leader
         (its dot is at canvas y~1114, inside the 1060 block's window). Swing
         clearance at 700 then runs its leader dead level into its dot at
         canvas y~690. Pinned by blueprint-birdseye-clearance.test.js ("blocks
         never overlap"), at more than one container width. */
      'swing-clearance': { side: 'right', ay: 700 },
      'jamb-depth':     { side: 'right', ay: 1060 },
      /* ay CENTRES THE FOUR-LINE BLOCK ON ITS LEADER. drawLabels runs a leader
         dead level only while the dot's y falls between the block's top and its
         LAST baseline. The dot sits at canvas y440; the block runs ay-38 to
         ay+170, so the window is 271..478 and 375 sits in the middle of it with
         ~104 units of slack each way.

         That slack is a consequence of the wrap, not luck. While the title was
         one line the window was only the title's own height (~38 units) and any
         type-size change would have kicked the leader into a PAD_Y jog. A test
         pins the level leader rather than this number, so re-tune here if the
         copy or the type ever changes. */
      'wall-clearance': { side: 'left',  ay: 375 },
      /* The pass-span dot sits at canvas x963 -- 61 units LEFT of centre (1024)
         -- so LEFT keeps the leader a short, mostly-vertical run instead of
         crossing the whole figure from the opposite gutter. ay 1150 clears
         wall-clearance's own four-line block above it (bottoms out well
         short of 1150 even wrapped) and stays clear of the frame-switch/
         measure-toggle row at y1615+ by a wide margin. */
      'pass-through':   { side: 'left',  ay: 1150 },
    },
    'iso-rh-in':  {
      'door-depth':     { side: 'left',  ay: 300 },
      // Mirror of iso-lh-in -- see the swing-clearance note there.
      'swing-clearance': { side: 'left', ay: 700 },
      'jamb-depth':     { side: 'left',  ay: 1060 },
      'wall-clearance': { side: 'right', ay: 375 },
      // Mirror of iso-lh-in: dot at canvas x1085, 61 units RIGHT of centre.
      'pass-through':   { side: 'right', ay: 1150 },
    },
    'iso-lh-out': {
      /* Door depth moved UP from 1220 to 680 (2026-09-25 evening) to make room
         for swing clearance. Both anchors sit on the open leaf's far end, door
         depth at its TOP (canvas y~722) and swing clearance lower down its
         edge (canvas y~1101), so each block now sits level with its own dot.
         Left where it was, door depth's leader climbed from 1220 to 722 and
         crossed the swing-clearance leader.
         Jamb depth 900 -> 880 (same as iso-rh-out): at a 370px container its
         block ran 14 units into pass-through's below it -- a pre-existing
         overlap the swing-clearance overlap test surfaced. */
      'door-depth': { side: 'left',  ay: 680 },
      'swing-clearance': { side: 'left', ay: 1080 },
      'jamb-depth': { side: 'right', ay: 880 },
      /* Dot at canvas x1041 -- 17 units RIGHT of centre, so RIGHT. ay 1150
         clears jamb-depth's two-line block above it (ay900) with room to
         spare and stays well short of the toggle row. */
      'pass-through': { side: 'right', ay: 1150 },
    },
    'iso-rh-out': {
      'door-depth': { side: 'right', ay: 490 },
      /* Dot at canvas ~(1190,1101), the mirror of iso-lh-out's. The right-
         gutter zoom clamp lifts this block at small containers (to ~997 at
         370px); the anchor's y was chosen so the dot stays inside the block's
         level-leader window at both 435 and 370 (pinned by test). */
      'swing-clearance': { side: 'right', ay: 1080 },
      'jamb-depth': { side: 'left',  ay: 880 },
      // Mirror of iso-lh-out: dot at canvas x1007, 17 units LEFT of centre.
      'pass-through': { side: 'left', ay: 1150 },
    },

    'left-inswing': {
      'door-depth':   { side: 'left',   ay: 300 },
      /* CENTRE, between the walls. From the left gutter the block sat INSIDE
         the left wall band, so the leader began within the hatch and ran along
         it to the jamb face. Approaching from the opening side crosses nothing. */
      'jamb-depth':   { side: 'center', ay: 1200 },
      // dx nudges it clear of the swing arc, which sweeps up through the centre here.
      'pass-through': { side: 'center', ay: 320, dx: 130 },
    },
    'right-inswing': {
      'door-depth':   { side: 'right',  ay: 300 },
      /* CENTRE, low. From the left gutter the leader had to traverse the whole
         drawing to reach the RIGHT jamb face and clipped the left wall band on
         the way out. Centred and dropped below the leaf it runs dead flat
         through the opening and touches nothing. ay is deliberately below the
         pass-through dot (y1151) so that leader does not end inside this block. */
      // Dropped clear of the pass-through span, which runs just above it.
      'jamb-depth':   { side: 'center', ay: 1290 },
      'pass-through': { side: 'left',   ay: 300 },
    },
    'left-outswing': {
      // door and jamb SWAPPED against the in-swing arrangement -- out-swing puts
      // the leaf below the wall, so the low slot is the one nearest its anchor.
      'door-depth':   { side: 'left',   ay: 1250 },
      /* CENTRE, not the left gutter. From the gutter the leader entered the
         left wall band through its TOP edge and travelled inside the hatch to
         reach the jamb face. Approaching from the centre lands on the face from
         the opening side, crossing nothing -- and the block still clears both
         bands horizontally (845..1202 sits between them). */
      'jamb-depth':   { side: 'center', ay: 320 },
      'pass-through': { side: 'right',  ay: 760 },
    },
    'right-outswing': {
      'door-depth':   { side: 'left',   ay: 1250 },
      /* ABOVE the wall it names. The block is two lines (~138 units), which is
         what lets it sit in the ~198-unit band between the frame rule (221) and
         the wall's top edge (419) -- a three-line block does not fit there. */
      'jamb-depth':   { side: 'right',  ay: 290 },
      'pass-through': { side: 'center', ay: 320 },
    },
  });

  /**
   * Anchor points for the ISOMETRIC birdseye frame, in each diagram's OWN
   * coordinate space (assets/pass-through-diagrams.js).
   *
   * 🚨 THESE ARE HAND-PLACED AND HAVE NO SEMANTIC HOOK. The diagrams are CAD
   * exports -- thousands of anonymous <path>/<line> children with no classes, so
   * unlike the drawn plan there is nothing to compute an anchor FROM. Each pair
   * was read off the rendered art against a coordinate grid. If the CAD is ever
   * re-exported these must be re-read; nothing will fail loudly.
   *
   * 🚨 PASS-THROUGH USED TO BE DELIBERATELY ABSENT HERE -- reversed 2026-09-11.
   * The reasoning was that the diagram already annotates itself with the floor
   * band and .ptw-rug-label, so a second callout would say it twice. Customer
   * feedback on the shipped drawing was that the bare in-art number, with no
   * title anywhere on the sheet, read as unlabelled -- a customer could see
   * "24″" on the floor but nothing told them what it measured. `plan:pass-span`
   * below gives it the same titled-block-plus-leader treatment wall clearance
   * already has, via `valueInArt: true` on its CALLOUT_SLOTS entry so the
   * number is still stated exactly once, at the point of measurement -- the
   * block supplies the title, the drawing supplies the value.
   *
   * Wall clearance is annotated by the diagram too and STILL gets an anchor --
   * that is not an inconsistency, it is the same pattern. The floor band is
   * self-describing on its own (it is drawn across the opening the number
   * measures, so a customer reads "24″" as the way through without being
   * told), but it gets a title anyway because leaving either annotation
   * unlabelled was the actual complaint, not merely the clearance's.
   */
  /* Anchors for the ISOMETRIC birdseye, in the diagram's own root coordinates.
     Keyed by the PLAN's anchor names on purpose so CALLOUT_SLOTS resolves against
     either drawing with no branch: `plan:leaf-top` is door depth,
     `plan:left-return` is jamb depth, `plan:wall-clearance` is the clearance
     dimension, `plan:pass-span` is the floor-band's .ptw-rug-label.

     🚨 ONLY THE LEFT-HAND PAIR IS STORED. The right-hand diagrams are the same
     art through `scale(-1,1) translate(-C,0)`, so their anchors are DERIVED at
     runtime as `C - x` with C read from that transform (248 in-swing, 280
     out-swing). Hand-authoring the mirrored pair instead put them out by up to 12
     units when read off a screenshot, and nothing would have failed loudly.

     Placed against the rendered art -- the CAD is thousands of anonymous paths
     with nothing to compute from. Re-read them if the CAD is re-exported. */
  const ISO_ANCHORS = Object.freeze({
    /* 'plan:wall-clearance' sits BESIDE the annotation's number, NOT on the
       dimension it measures. The obvious anchor is the dimension's midpoint
       (53.34,95) -- the CAD runs the line (62,100) to (44.68,90) and rises its
       own dashed extension from exactly there -- and that is what shipped
       first. It is wrong: the leader dot is 7.6 diagram units across, so it
       lands squarely on the junction and HIDES the span marker the annotation
       exists to draw. The dot has to attach to the label, not cover the ruler.

       So: x4 y54 puts it against the left edge of the "1 3/4″" glyph box at
       that glyph box's own vertical middle. Measured live rather than guessed,
       because the value's WIDTH varies -- getBBox on the rendered label gives
       y 41.08..66.43 (middle 53.8, hence y54) and a left edge of x14.54 for
       every value with a whole-inch part, x27.52 for the sub-inch "3/4″". A
       dot centred at x4 spans x-3.6..11.6, so it clears 14.54 by ~3 units and
       clears the span marker (x40.7..66, y66..104) on BOTH axes.

       It overhangs the diagram's own white ground by ~3.6 units. That is
       invisible: .md-bp-frame[data-frame='birdseye'] is --brand-white and the
       CAD's background rect is white, so they are the same ground.

       🚨 THE MIRROR IS WHY THIS NEEDS NO PER-HAND ENTRY. Right-hand anchors are
       derived as `C - x`, so x4 becomes x244 -- the RIGHT edge of the mirrored
       label. The block swaps gutters with the handing at the same time, so the
       dot always ends up on the side the leader arrives from.

       Absent from 'lh-out' because the out-swing CAD carries no
       .ptw-wc-annotation group at all -- an out-swing door swings away from the
       wall. Absent is the correct answer there, not an omission. */
    /* 'plan:pass-span' sits BELOW the ".ptw-rug-label" glyph box, same principle
       as 'plan:wall-clearance' above -- the dot has to attach to the label
       without covering the number it names. pass-through-diagrams.js authors
       the label at text-anchor=middle / dominant-baseline=middle / font-size 20
       (its own diagram-local units): lh-in at (84,440), lh-out at (182,395).
       +24 in y clears the glyph box (a 3-character value like "24″" runs
       roughly 10 units tall at that font-size) while staying inside the rug
       band the label already sits on, so the dot reads as "on the rug, by the
       number" rather than floating off it. Both diagrams place the label well
       clear of their viewBox edges in every direction, so the offset cannot
       walk it into the clip boundary. */
    /* 'plan:swing-clearance' sits ON the open leaf's FAR vertical edge -- the
       "furthest point on the door when open" that sheet B24 measures to. Read
       off the CAD's own line list rather than a screenshot: lh-in's far edge
       is the x194.65 line (y59..162 above the head casing, continuing
       y176..328 below it); lh-out's is the x107.1/109.32 pair (y129..396).
       y is chosen so each block's leader runs level (see
       BIRDSEYE_SLOTS_BY_HANDING) -- on out-swing that includes the mirrored
       rh-out block after the right-gutter zoom clamp lifts it -- and, on
       lh-out, clear above the rug band (~y361 at that x). Mirrors like every
       other anchor. */
    'lh-in':  {
      'plan:leaf-top': { x: 157, y: 52 },
      'plan:left-return': { x: 180, y: 313 },
      'plan:wall-clearance': { x: 4, y: 54 },
      'plan:pass-span': { x: 84, y: 464 },
      'plan:swing-clearance': { x: 194.65, y: 150 },
    },
    'lh-out': {
      'plan:leaf-top': { x: 85,  y: 122 },
      'plan:left-return': { x: 276, y: 266 },
      'plan:pass-span': { x: 182, y: 419 },
      'plan:swing-clearance': { x: 108.2, y: 275 },
    },
  });

  const MEASUREMENT_CATEGORY_FRAME = Object.freeze({
    Width: 'elevation',
    Height: 'elevation',
    'Door Depth': 'birdseye',
    'Jamb Depth': 'birdseye',
    'Door Handing': 'birdseye',
  });

  /** Categories that survive with the blueprint on. Everything else is hidden. */
  const BLUEPRINT_VISIBLE_CATEGORIES = Object.freeze([]);

  window.MurphyDoorConfigurator.blueprintGeometry = Object.freeze({
    CANVAS,
    OPENING,
    DOOR_UNIT,
    PIXELS_PER_INCH,
    BASE_RENDER_RO,
    traces,
    trimProfiles,
    MEASUREMENT_CATEGORIES,
    MEASUREMENT_CATEGORY_FRAME,
    BIRDSEYE_SLOTS_BY_HANDING,
    ISO_ANCHORS,
    BLUEPRINT_VISIBLE_CATEGORIES,
    GUTTER_L,
    GUTTER_R,
    BRACE,
    PLAN,
    BIRDSEYE,
    BLUEPRINT_SILHOUETTES,
    CALLOUT_SLOTS,
  });
})(window);
